/**
 * spec-130 phase 5 — admin Group Members page.
 *
 * Route: /admin/groups/:groupId/members
 *
 * Manages per-group GroupMembership rows separately from the system-
 * wide User.role and the chapter Membership rows.
 *
 *   - Members table with role + actions (set admin/member, remove).
 *   - Add member by user search (existing usersSearchApi).
 *   - Pending join requests panel for joinPolicy='request' groups.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  ApiClientError,
  groupMembersApi,
  groupsApi,
  usersSearchApi,
  type GroupMemberRow,
  type GroupJoinRequestRow,
  type UserSearchResult,
} from '@/services/api';
import type { Group } from '@/types';

export function GroupMembersPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<Group | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [members, setMembers] = useState<GroupMemberRow[]>([]);
  const [requests, setRequests] = useState<GroupJoinRequestRow[]>([]);
  const [requestBusyId, setRequestBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!groupId) return;
    setError(null);
    try {
      const [g, m, jr] = await Promise.all([
        groupsApi.get(groupId),
        groupMembersApi.list(groupId),
        groupMembersApi.listJoinRequests(groupId, 'pending').catch(() => [] as GroupJoinRequestRow[]),
      ]);
      setGroup(g);
      setMembers(m);
      setRequests(jr);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Failed to load group');
    }
  }, [groupId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleRoleChange(userId: string, next: 'member' | 'admin') {
    if (!groupId) return;
    setBusyUserId(userId);
    try {
      await groupMembersApi.setRole(groupId, userId, next);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setBusyUserId(null);
    }
  }

  async function handleRemove(userId: string, name?: string) {
    if (!groupId) return;
    if (!window.confirm(`Remove ${name ?? 'this member'} from the group?`)) return;
    setBusyUserId(userId);
    try {
      await groupMembersApi.remove(groupId, userId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setBusyUserId(null);
    }
  }

  async function decide(id: string, action: 'approve' | 'reject') {
    if (!groupId) return;
    setRequestBusyId(id);
    try {
      if (action === 'approve') await groupMembersApi.approveJoinRequest(groupId, id);
      else await groupMembersApi.rejectJoinRequest(groupId, id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action}`);
    } finally {
      setRequestBusyId(null);
    }
  }

  if (!group) {
    return (
      <div className="p-6">
        <p className="text-white/60">{error ?? 'Loading…'}</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <Link to="/admin/groups" className="text-white/60 hover:text-white text-sm">
            ← Back to Groups
          </Link>
          <h1 className="text-2xl font-display font-bold text-white mt-1">{group.name} — members</h1>
          <p className="text-white/60 text-sm">
            visibility: <span className="font-mono">{group.visibility ?? 'listed'}</span> ·
            join: <span className="font-mono">{group.joinPolicy ?? 'open'}</span>
            {group.isDefault && <span className="ml-2 text-yellow-400">default group</span>}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/groups')}
          className="btn-secondary text-sm"
        >
          Close
        </button>
      </header>

      {error && (
        <div className="card border-red-500/20 bg-red-500/10">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <AddMemberForm groupId={groupId!} onAdded={() => void reload()} />

      <section className="card">
        <h2 className="text-lg font-semibold text-white mb-3">Members ({members.length})</h2>
        {members.length === 0 ? (
          <p className="text-white/60 text-sm">
            No explicit members yet.
            {group.joinPolicy === 'inherit_chapter'
              ? ' This group inherits all chapter members automatically.'
              : ' Add the first member above.'}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {members.map((m) => (
              <li key={m.userId} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-b-0">
                <div className="min-w-0 flex-1">
                  <p className="text-white font-medium truncate">{m.displayName ?? m.userId.slice(0, 8) + '…'}</p>
                  <p className="text-xs text-white/40 truncate">{m.email ?? '—'} · joined {new Date(m.joinedAt).toLocaleDateString()}</p>
                </div>
                <select
                  value={m.role}
                  onChange={(e) => handleRoleChange(m.userId, e.target.value as 'member' | 'admin')}
                  disabled={busyUserId === m.userId}
                  className="input py-1 px-2 text-xs"
                >
                  <option value="member">Member</option>
                  <option value="admin">Group admin</option>
                </select>
                <button
                  type="button"
                  onClick={() => handleRemove(m.userId, m.displayName)}
                  disabled={busyUserId === m.userId}
                  className="text-xs px-2 py-1 bg-red-500/20 text-red-300 border border-red-500/30 rounded hover:bg-red-500/30"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {group.joinPolicy === 'request' && (
        <section className="card">
          <h2 className="text-lg font-semibold text-white mb-3">
            Pending join requests {requests.length > 0 && <span className="text-yellow-400 text-sm">({requests.length})</span>}
          </h2>
          {requests.length === 0 ? (
            <p className="text-white/60 text-sm">No pending requests.</p>
          ) : (
            <ul className="space-y-2">
              {requests.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium truncate">{r.user?.displayName ?? (r.userId.slice(0, 8) + '…')}</p>
                    <p className="text-xs text-white/40 truncate">{r.user?.email ?? '—'}</p>
                    {r.requestNote && <p className="text-xs text-white/60 mt-0.5 line-clamp-2">{r.requestNote}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => decide(r.id, 'approve')}
                      disabled={requestBusyId === r.id}
                      className="text-xs px-2 py-1 bg-green-500/20 text-green-300 border border-green-500/30 rounded hover:bg-green-500/30"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => decide(r.id, 'reject')}
                      disabled={requestBusyId === r.id}
                      className="text-xs px-2 py-1 bg-red-500/20 text-red-300 border border-red-500/30 rounded hover:bg-red-500/30"
                    >
                      Reject
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function AddMemberForm({ groupId, onAdded }: { groupId: string; onAdded: () => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<UserSearchResult | null>(null);
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    usersSearchApi.search(term, 10).then((rows) => {
      if (!cancelled) setResults(rows);
    }).catch(() => undefined).finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [q]);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    if (!picked) return;
    setBusy(true);
    setError(null);
    try {
      await groupMembersApi.add(groupId, { userId: picked.id, role });
      setPicked(null);
      setQ('');
      setResults([]);
      onAdded();
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
      else setError(err instanceof Error ? err.message : 'Add failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h2 className="text-lg font-semibold text-white mb-3">Add member</h2>
      <form className="space-y-2" onSubmit={handleAdd}>
        <div>
          <label className="block text-xs text-white/60 mb-1">Search users by name or email</label>
          <input
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPicked(null); }}
            placeholder="Type at least 2 characters…"
            className="input"
          />
          {searching && <p className="text-xs text-white/40 mt-1">Searching…</p>}
          {!picked && results.length > 0 && (
            <ul className="mt-1 max-h-44 overflow-auto rounded border border-white/10 bg-black/30">
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => { setPicked(r); setQ(r.displayName); setResults([]); }}
                    className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm text-white/90"
                  >
                    <span className="font-medium">{r.displayName}</span>
                    <span className="text-white/40 ml-2 text-xs">{r.email ?? '—'}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {picked && (
          <div className="flex items-center gap-3">
            <p className="text-sm text-white/80">
              Add <span className="text-white font-medium">{picked.displayName}</span> as
            </p>
            <select value={role} onChange={(e) => setRole(e.target.value as 'member' | 'admin')} className="input py-1 px-2 text-sm w-auto">
              <option value="member">Member</option>
              <option value="admin">Group admin</option>
            </select>
            <button type="submit" disabled={busy} className="btn-primary text-sm px-3 py-1.5">
              {busy ? 'Adding…' : 'Add'}
            </button>
          </div>
        )}
        {error && <p className="text-sm text-red-400">{error}</p>}
      </form>
    </section>
  );
}

export default GroupMembersPage;
